import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
	ArrowLeft,
	Star,
	MapPin,
	Shield,
	Calendar,
	DollarSign,
	Wrench,
	Clock,
	MessageSquare,
} from "lucide-react";
import { api } from "@/lib/api";
import type { Tool, User, Booking } from "@/lib/types";
import { CATEGORY_LABELS, CONDITION_LABELS } from "@/lib/types";
import { AvailabilityCalendar } from "./AvailabilityCalendar";
import { TrustScore } from "./TrustScore";

interface ToolDetailProps {
	toolId: string;
	onBack: () => void;
	onBook: (toolId: string, start: string, end: string) => void;
	onViewProfile: (userId: string) => void;
}

export function ToolDetail({ toolId, onBack, onBook, onViewProfile }: ToolDetailProps) {
	const [tool, setTool] = useState<(Tool & { owner?: User; bookings?: Booking[] }) | null>(null);
	const [ownerTrust, setOwnerTrust] = useState<number>(0);
	const [selectedStart, setSelectedStart] = useState<string>("");
	const [selectedEnd, setSelectedEnd] = useState<string>("");
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadTool();
	}, [toolId]);

	async function loadTool() {
		setLoading(true);
		try {
			const data = await api.getTool(toolId);
			setTool(data);
			if (data.owner) {
				const ownerData = await api.getUser(data.owner.id);
				setOwnerTrust(ownerData.trustScore);
			}
		} catch (e) {
			console.error("Failed to load tool:", e);
		} finally {
			setLoading(false);
		}
	}

	if (loading) {
		return (
			<div className="space-y-4">
				<div className="h-8 w-32 bg-muted rounded animate-pulse" />
				<div className="h-64 bg-muted rounded-lg animate-pulse" />
				<div className="h-32 bg-muted rounded-lg animate-pulse" />
			</div>
		);
	}

	if (!tool) {
		return (
			<div className="text-center py-12">
				<p className="text-muted-foreground">Tool not found</p>
				<Button variant="outline" onClick={onBack} className="mt-4">
					Go back
				</Button>
			</div>
		);
	}

	const days =
		selectedStart && selectedEnd
			? Math.max(
					1,
					Math.ceil(
						(new Date(selectedEnd).getTime() -
							new Date(selectedStart).getTime()) /
							(1000 * 60 * 60 * 24)
					) + 1
				)
			: 0;
	const totalCost = days * tool.dailyRate;

	return (
		<div className="space-y-6">
			{/* Back button */}
			<Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
				<ArrowLeft className="w-4 h-4" />
				Back to listings
			</Button>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Main content */}
				<div className="lg:col-span-2 space-y-6">
					{/* Tool image */}
					<div className="h-56 sm:h-72 bg-gradient-to-br from-muted to-muted/50 rounded-lg flex items-center justify-center">
						<Wrench className="w-20 h-20 text-muted-foreground/20" />
					</div>

					{/* Tool info */}
					<div>
						<div className="flex items-start justify-between gap-4">
							<div>
								<h1 className="text-xl font-bold">{tool.name}</h1>
								<div className="flex items-center gap-2 mt-1.5">
									<Badge variant="secondary">
										{CATEGORY_LABELS[tool.category]}
									</Badge>
									<Badge variant="outline">
										{CONDITION_LABELS[tool.condition]}
									</Badge>
								</div>
							</div>
							<div className="text-right shrink-0">
								<p className="text-2xl font-bold">${tool.dailyRate}</p>
								<p className="text-xs text-muted-foreground">per day</p>
							</div>
						</div>

						<p className="text-sm text-muted-foreground mt-4 leading-relaxed">
							{tool.description}
						</p>

						<div className="flex items-center gap-4 mt-4">
							<div className="flex items-center gap-1">
								<Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
								<span className="text-sm font-medium">{tool.rating}</span>
								<span className="text-sm text-muted-foreground">
									({tool.reviewCount} reviews)
								</span>
							</div>
							<div className="flex items-center gap-1 text-sm text-muted-foreground">
								<MapPin className="w-4 h-4" />
								{tool.neighborhood}
							</div>
						</div>
					</div>

					<Separator />

					{/* Owner info */}
					{tool.owner && (
						<Card>
							<CardContent className="p-4">
								<div className="flex items-center justify-between">
									<button
										onClick={() => onViewProfile(tool.owner!.id)}
										className="flex items-center gap-3 hover:opacity-80 transition-opacity"
									>
										<Avatar className="h-10 w-10">
											<AvatarImage src={tool.owner.avatar} />
											<AvatarFallback>
												{tool.owner.name[0]}
											</AvatarFallback>
										</Avatar>
										<div>
											<p className="text-sm font-medium">
												{tool.owner.name}
											</p>
											<p className="text-xs text-muted-foreground flex items-center gap-1">
												<MapPin className="w-3 h-3" />
												{tool.owner.neighborhood}
											</p>
										</div>
									</button>
									<div className="flex items-center gap-3">
										<TrustScore score={ownerTrust || tool.owner.trustScore} size="sm" />
									</div>
								</div>
								<div className="grid grid-cols-3 gap-4 mt-4 pt-3 border-t border-border">
									<div className="text-center">
										<p className="text-lg font-semibold">
											{tool.owner.totalLends}
										</p>
										<p className="text-xs text-muted-foreground">Lends</p>
									</div>
									<div className="text-center">
										<p className="text-lg font-semibold">
											{tool.owner.responseRate}%
										</p>
										<p className="text-xs text-muted-foreground">
											Response
										</p>
									</div>
									<div className="text-center">
										<p className="text-lg font-semibold">
											{tool.owner.trustScore}
										</p>
										<p className="text-xs text-muted-foreground">
											Trust
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					)}
				</div>

				{/* Sidebar - Booking */}
				<div className="space-y-4">
					<Card>
						<CardContent className="p-4 space-y-4">
							<h3 className="font-semibold flex items-center gap-2">
								<Calendar className="w-4 h-4" />
								Select dates
							</h3>
							<AvailabilityCalendar
								availability={tool.availability}
								selectedStart={selectedStart}
								selectedEnd={selectedEnd}
								onSelectRange={(start, end) => {
									setSelectedStart(start);
									setSelectedEnd(end);
								}}
							/>

							{selectedStart && selectedEnd && (
								<div className="space-y-2 pt-2 border-t border-border">
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground">
											${tool.dailyRate} x {days} day{days > 1 ? "s" : ""}
										</span>
										<span>${totalCost}</span>
									</div>
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground flex items-center gap-1">
											<Shield className="w-3 h-3" />
											Refundable deposit
										</span>
										<span>${tool.deposit}</span>
									</div>
									<Separator />
									<div className="flex justify-between font-semibold">
										<span>Total due</span>
										<span>${totalCost + tool.deposit}</span>
									</div>
									<p className="text-[10px] text-muted-foreground">
										Deposit returned after tool is returned in good condition
									</p>
								</div>
							)}

							<Button
								className="w-full"
								disabled={!selectedStart || !selectedEnd}
								onClick={() => onBook(tool.id, selectedStart, selectedEnd)}
							>
								{selectedStart && selectedEnd
									? `Request to Rent - $${totalCost + tool.deposit}`
									: "Select dates to book"}
							</Button>
						</CardContent>
					</Card>

					<Card>
						<CardContent className="p-4 space-y-3">
							<h3 className="font-semibold text-sm">Rental details</h3>
							<div className="space-y-2 text-sm">
								<div className="flex items-center gap-2 text-muted-foreground">
									<DollarSign className="w-4 h-4 shrink-0" />
									<span>
										${tool.deposit} refundable security deposit
									</span>
								</div>
								<div className="flex items-center gap-2 text-muted-foreground">
									<Clock className="w-4 h-4 shrink-0" />
									<span>Pick up and drop off locally</span>
								</div>
								<div className="flex items-center gap-2 text-muted-foreground">
									<Shield className="w-4 h-4 shrink-0" />
									<span>Damage protection included</span>
								</div>
								<div className="flex items-center gap-2 text-muted-foreground">
									<MessageSquare className="w-4 h-4 shrink-0" />
									<span>Message lender before booking</span>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
